import { useEffect, useRef, useState } from 'react';
import { stripeFor as wiredStripeFor } from '../lib/stripeCard';
import SafeAndSecure from '../components/SafeAndSecure';
import './cardForm.css';

/**
 * The card screen (InGo ADR 0050): paying by card inside InGo's own screen.
 * Rio, 11 Oct 2026: "we should be having a full control of our system in app".
 *
 * - **Stripe's own fields** hold the card: number, expiry and security code
 *   are Stripe's frames, so the number goes from the phone to Stripe and
 *   never through InGo's code. Only the name on the card is InGo's field.
 * - **The card drawn above them** follows what Stripe's fields say, never
 *   the digits: the brand once Stripe knows it, the name, which fields are
 *   done, and a turn to its back while the security code is typed.
 * - **Pay** confirms the server's PaymentIntent with its client secret. A
 *   bank's 3-D Secure check opens over this screen, in Stripe's frame.
 *   What Stripe answers here only says what to show next: "paid" is the
 *   server's own word, asked of Stripe, read by the payment screen's poll.
 */

/** Stripe's fields are its frames: they draw with this, the app's own type and colours. */
const FIELD_STYLE = {
  base: {
    fontSize: '17px',
    color: '#1f2937',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    '::placeholder': { color: '#9ca3af' },
  },
  invalid: { color: '#b91c1c' },
};

/** Each brand Stripe names, as the card shows it. */
const BRANDS = {
  visa: 'VISA',
  mastercard: 'Mastercard',
  amex: 'AMEX',
  discover: 'DISCOVER',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

/** What the client reads when Stripe.js could not be loaded. */
export const STRIPE_UNREACHABLE = 'The card form could not load. Check your connection, then try again.';

/** What the client reads when Pay is pressed before the card is whole. */
export const CARD_UNFINISHED = 'Finish the card first: its number, expiry date and security code.';

export const NAME_NEEDED = 'Type the name on the card.';

/** The four fields' state, from Stripe's change events and the name. */
const EMPTY = { number: false, expiry: false, cvc: false };

function Chip() {
  return (
    <svg className="cc-card__chip" width="40" height="30" viewBox="0 0 40 30" aria-hidden>
      <rect x="0.5" y="0.5" width="39" height="29" rx="6" fill="#e9c46a" stroke="#c9a227" />
      <path d="M13 1v28M27 1v28M1 10h12M27 10h12M1 20h12M27 20h12" stroke="#b8891c" strokeWidth="1.2" fill="none" />
    </svg>
  );
}

function BrandMark({ brand }) {
  if (brand === 'mastercard') {
    return (
      <span className="cc-card__brand cc-card__brand--mc" aria-hidden>
        <span />
        <span />
      </span>
    );
  }

  return <span className="cc-card__brand">{BRANDS[brand] || ''}</span>;
}

export default function CardForm({ stripe: card, amount, onConfirmed, onPayAnotherWay, stripeFor = wiredStripeFor }) {
  const [state, setState] = useState('loading');
  const [load, setLoad] = useState(0);
  const [brand, setBrand] = useState('unknown');
  const [onBack, setOnBack] = useState(false);
  const [name, setName] = useState('');
  const [done, setDone] = useState(EMPTY);
  const [problem, setProblem] = useState('');
  const [paying, setPaying] = useState(false);
  const numberRef = useRef(null);
  const expiryRef = useRef(null);
  const cvcRef = useRef(null);
  const stripeRef = useRef(null);
  const fieldsRef = useRef(null);
  const publishableKey = card?.publishable_key;

  useEffect(() => {
    let gone = false;
    let fields = null;
    setState('loading');
    stripeFor(publishableKey)
      .then((stripe) => {
        if (gone) return;
        const elements = stripe.elements();
        fields = {
          number: elements.create('cardNumber', { style: FIELD_STYLE, showIcon: false, placeholder: '1234 1234 1234 1234' }),
          expiry: elements.create('cardExpiry', { style: FIELD_STYLE }),
          cvc: elements.create('cardCvc', { style: FIELD_STYLE }),
        };
        fields.number.mount(numberRef.current);
        fields.expiry.mount(expiryRef.current);
        fields.cvc.mount(cvcRef.current);
        const follow = (key) => (event) => {
          setDone((now) => ({ ...now, [key]: Boolean(event?.complete) }));
          setProblem(event?.error?.message || '');
        };
        fields.number.on('change', (event) => {
          setBrand(event?.brand || 'unknown');
          follow('number')(event);
        });
        fields.expiry.on('change', follow('expiry'));
        fields.cvc.on('change', follow('cvc'));
        // American Express keeps its code on the front.
        fields.cvc.on('focus', () => setOnBack(true));
        fields.cvc.on('blur', () => setOnBack(false));
        stripeRef.current = stripe;
        fieldsRef.current = fields;
        setState('ready');
      })
      .catch(() => {
        if (!gone) setState('failed');
      });

    return () => {
      gone = true;
      if (fields) Object.values(fields).forEach((field) => field.destroy());
      fieldsRef.current = null;
    };
  }, [publishableKey, stripeFor, load]);

  const pay = async (e) => {
    e.preventDefault();
    if (state !== 'ready' || paying) return;
    if (!name.trim()) {
      setProblem(NAME_NEEDED);
      return;
    }
    if (!done.number || !done.expiry || !done.cvc) {
      setProblem(CARD_UNFINISHED);
      return;
    }
    setPaying(true);
    setProblem('');
    try {
      const result = await stripeRef.current.confirmCardPayment(card.client_secret, {
        payment_method: { card: fieldsRef.current.number, billing_details: { name: name.trim() } },
      });
      // Already paid, by this screen a moment ago: the server's poll says the rest.
      const settled = result?.paymentIntent?.status || result?.error?.payment_intent?.status;
      if (result?.error && settled !== 'succeeded' && settled !== 'processing') {
        setProblem(result.error.message || 'The card was not accepted. Check it, or use another card.');
        return;
      }
      onConfirmed(settled || 'processing');
    } catch {
      setProblem('The card could not be sent. Check your connection, then press Pay again.');
    } finally {
      setPaying(false);
    }
  };

  const showBrand = brand !== 'unknown' && BRANDS[brand] ? brand : 'none';

  return (
    <main className="pay-page" aria-labelledby="pay-title">
      <header className="pay-head">
        <span>Card</span>
        <span>{amount}</span>
      </header>
      <h1 id="pay-title" className="pay-title cc-title">
        Pay {amount} by card
      </h1>

      <div className={`cc-card cc-card--${showBrand}${onBack && brand !== 'amex' ? ' cc-card--back' : ''}`} aria-hidden data-testid="card-picture">
        <div className="cc-card__turn">
          <div className="cc-card__face cc-card__front">
            <div className="cc-card__top">
              <Chip />
              <BrandMark brand={showBrand} />
            </div>
            <p className={`cc-card__number${done.number ? ' cc-card__number--done' : ''}`}>•••• •••• •••• ••••</p>
            <div className="cc-card__bottom">
              <span className="cc-card__name">{name.trim() ? name.trim().toUpperCase() : 'NAME ON CARD'}</span>
              <span className={`cc-card__expiry${done.expiry ? ' cc-card__expiry--done' : ''}`}>
                <small>Expires</small>
                ••/••
              </span>
            </div>
          </div>
          <div className="cc-card__face cc-card__back">
            <span className="cc-card__stripe" />
            <div className="cc-card__sign">
              <span className={`cc-card__cvc${done.cvc ? ' cc-card__cvc--done' : ''}`}>•••</span>
            </div>
            <p className="cc-card__hint">The security code is the 3 digits on the back.</p>
          </div>
        </div>
      </div>

      <form className="cc-form" onSubmit={pay} noValidate>
        <div className="pay-field">
          <label className="pay-field__label" htmlFor="cc-name">
            Name on card
          </label>
          <input
            id="cc-name"
            className="pay-field__input"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (problem === NAME_NEEDED) setProblem('');
            }}
            autoComplete="cc-name"
            autoCapitalize="characters"
            maxLength={60}
            disabled={paying}
          />
        </div>
        <div className="pay-field">
          {/* Stripe's field is its frame: the label focuses it. */}
          <span className="pay-field__label" id="cc-number-label" onClick={() => fieldsRef.current?.number.focus()}>
            Card number
          </span>
          <div className="cc-stripe" ref={numberRef} role="group" aria-labelledby="cc-number-label" />
        </div>
        <div className="cc-row">
          <div className="pay-field">
            <span className="pay-field__label" id="cc-expiry-label" onClick={() => fieldsRef.current?.expiry.focus()}>
              Expiry date
            </span>
            <div className="cc-stripe" ref={expiryRef} role="group" aria-labelledby="cc-expiry-label" />
          </div>
          <div className="pay-field">
            <span className="pay-field__label" id="cc-cvc-label" onClick={() => fieldsRef.current?.cvc.focus()}>
              Security code
            </span>
            <div className="cc-stripe" ref={cvcRef} role="group" aria-labelledby="cc-cvc-label" />
          </div>
        </div>

        {state === 'failed' ? (
          <p className="cc-problem" role="alert">
            {STRIPE_UNREACHABLE}
          </p>
        ) : null}
        {problem ? (
          <p className="cc-problem" role="alert">
            {problem}
          </p>
        ) : null}

        <SafeAndSecure className="cc-safe" />

        <div className="pay-actions cc-actions">
          {state === 'failed' ? (
            <button type="button" className="pay-btn pay-btn--primary" onClick={() => setLoad((n) => n + 1)}>
              Try again
            </button>
          ) : (
            <button type="submit" className="pay-btn pay-btn--primary" disabled={state !== 'ready' || paying}>
              {paying ? 'Paying…' : `Pay ${amount}`}
            </button>
          )}
          <button type="button" className="pay-btn pay-btn--quiet" onClick={onPayAnotherWay} disabled={paying}>
            Pay another way
          </button>
        </div>
      </form>
    </main>
  );
}
